import { useMemo } from 'react'
import { ChevronDown } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { EmptyValue } from '@/components/custom/empty-value'
import { createMemoCell } from '@/lib/create-memo-cell'
import { formatEnumValue } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import type { CellProps } from '../types'

type BadgeVariant = 'outline' | 'outline-plain' | 'solid'

interface CellBadgeProps extends CellProps<string | null | undefined> {
  /** Available options for styling */
  options?: string[]
  /** Custom color map for specific values */
  colorMap?: Record<string, string>
  /** Badge style variant */
  variant?: BadgeVariant
  /** Show a chevron icon inside the badge */
  showChevron?: boolean
}

type StatusBadgeVariant = 'green' | 'red' | null

// Default color palette for status-like values
const DEFAULT_COLORS: Record<string, string> = {
  // Status colors
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  in_progress: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  processing: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  on_hold: 'bg-yellow-100 text-yellow-800 border-yellow-200',

  inactive: 'bg-gray-100 text-gray-800 border-gray-200',
  draft: 'bg-gray-100 text-gray-800 border-gray-200',
  archived: 'bg-gray-100 text-gray-800 border-gray-200',

  error: 'bg-red-100 text-red-800 border-red-200',
  failed: 'bg-red-100 text-red-800 border-red-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200',
  exited: 'bg-red-100 text-red-800 border-red-200',

  // Priority colors
  high: 'bg-red-100 text-red-800 border-red-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-blue-100 text-blue-800 border-blue-200',
}

// Color palette for auto-assigning colors to options
const COLOR_PALETTE = [
  'bg-blue-100 text-blue-800 border-blue-200',
  'bg-purple-100 text-purple-800 border-purple-200',
  'bg-pink-100 text-pink-800 border-pink-200',
  'bg-indigo-100 text-indigo-800 border-indigo-200',
  'bg-cyan-100 text-cyan-800 border-cyan-200',
  'bg-teal-100 text-teal-800 border-teal-200',
  'bg-orange-100 text-orange-800 border-orange-200',
  'bg-lime-100 text-lime-800 border-lime-200',
]

// Solid variant base style
const SOLID_STYLE = 'bg-slate-500/10 text-primary border-transparent'

function CellBadgeBase({
  value,
  options = [],
  colorMap = {},
  variant = 'outline',
  showChevron = false,
  className,
}: CellBadgeProps) {
  const { displayValue, colorClass, statusVariant } = useMemo(() => {
    if (value === null || value === undefined || value === '') {
      return { displayValue: null, colorClass: '', statusVariant: null }
    }

    const displayValue =
      variant === 'solid' ? String(value) : formatEnumValue(String(value))
    const normalizedValue = String(value).toLowerCase().replace(/\s+/g, '_')

    const statusVariant: StatusBadgeVariant = [
      'active',
      'completed',
      'success',
      'approved',
    ].includes(normalizedValue)
      ? 'green'
      : ['error', 'failed', 'rejected', 'cancelled', 'exited'].includes(
            normalizedValue
          )
        ? 'red'
        : null

    if (statusVariant) {
      return { displayValue, colorClass: '', statusVariant }
    }

    // Solid variant uses fixed style
    if (variant === 'solid') {
      return { displayValue, colorClass: SOLID_STYLE, statusVariant: null }
    }

    // Check custom color map first
    if (colorMap[value] || colorMap[normalizedValue]) {
      return {
        displayValue,
        colorClass: colorMap[value] || colorMap[normalizedValue],
        statusVariant: null,
      }
    }

    // Check default colors
    if (DEFAULT_COLORS[normalizedValue]) {
      return {
        displayValue,
        colorClass: DEFAULT_COLORS[normalizedValue],
        statusVariant: null,
      }
    }

    // Auto-assign color based on position in options
    if (options.length > 0) {
      const index = options.findIndex(
        (opt) => opt.toLowerCase() === normalizedValue
      )
      if (index >= 0) {
        return {
          displayValue,
          colorClass: COLOR_PALETTE[index % COLOR_PALETTE.length],
          statusVariant: null,
        }
      }
    }

    // Default fallback color
    return {
      displayValue,
      colorClass: 'bg-gray-100 text-gray-800 border-gray-200',
      statusVariant: null,
    }
  }, [value, options, colorMap, variant])

  if (displayValue === null) {
    return <EmptyValue />
  }

  // Solid variant has specific sizing per design specs
  // height: 20px, padding: 0 6px, gap: 10px, rounded-full, font: 10px/20px
  if (variant === 'solid') {
    return (
      <span
        className={cn(
          'inline-flex items-center justify-center h-5 rounded-full px-1.5 text-[10px] leading-5 font-normal whitespace-nowrap',
          showChevron && 'gap-0.5 pr-1',
          colorClass,
          className
        )}
      >
        {displayValue}
        {showChevron && (
          <ChevronDown className="size-2.5 text-muted-foreground shrink-0" />
        )}
      </span>
    )
  }

  // Plain outline variant - border only, no background colors
  // Uses same sizing as solid: height 20px, font 10px
  if (variant === 'outline-plain') {
    return (
      <Badge
        variant="outline"
        className={cn(
          'h-5 px-1.5 text-[10px] leading-5 font-normal whitespace-nowrap',
          showChevron && 'gap-0.5 pr-1',
          className
        )}
      >
        {displayValue}
        {showChevron && (
          <ChevronDown className="size-2.5 text-muted-foreground shrink-0" />
        )}
      </Badge>
    )
  }

  return (
    <Badge
      variant={statusVariant ?? 'outline'}
      className={cn(
        'font-medium text-xs whitespace-nowrap',
        colorClass,
        className
      )}
    >
      {displayValue}
    </Badge>
  )
}

export const CellBadge = createMemoCell(CellBadgeBase, 'CellBadge')
